import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";

export default function ResetPassword() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setUser, setTokens } = useAuth();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError(t("password_too_short"));
      return;
    }
    if (password !== confirm) {
      setError(t("password_mismatch"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setTokens(data.access_token, data.refresh_token);
      setUser(data.user);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="mx-auto max-w-md px-6 py-20" data-testid="reset-password-page">
        <h1 className="font-serif text-3xl sm:text-4xl text-zinc-50 font-light tracking-tight leading-none">
          {t("reset_invalid_title")}
        </h1>
        <p className="mt-5 text-zinc-400 font-light leading-relaxed" data-testid="reset-password-invalid">
          {t("reset_invalid_body")}
        </p>
        <div className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
          <Link
            to="/forgot-password"
            className="text-zinc-100 hover:text-white underline-offset-4 hover:underline"
            data-testid="reset-password-restart-link"
          >
            {t("forgot_submit")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-6 py-20" data-testid="reset-password-page">
      <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-4">
        {t("brand")}
      </div>
      <h1 className="font-serif text-4xl sm:text-5xl text-zinc-50 font-light tracking-tight leading-none">
        {t("reset_title")}
      </h1>
      <p className="mt-5 text-zinc-400 font-light leading-relaxed">
        {t("reset_lead")}
      </p>

      <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="reset-password-form">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            {t("reset_new_password")}
          </label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 w-full bg-transparent border border-zinc-800 px-4 py-3 text-zinc-100 font-light focus:outline-none focus:border-zinc-500"
            data-testid="reset-password-input"
            autoFocus
          />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            {t("reset_confirm_password")}
          </label>
          <input
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="mt-2 w-full bg-transparent border border-zinc-800 px-4 py-3 text-zinc-100 font-light focus:outline-none focus:border-zinc-500"
            data-testid="reset-password-confirm-input"
          />
        </div>
        {error && (
          <div className="border border-red-900 bg-red-950/30 text-red-300 px-4 py-3 text-sm" data-testid="reset-password-error">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={busy}
          className="w-full bg-sienna text-white py-3 text-sm font-light hover:bg-sienna-400 disabled:opacity-60 transition-colors"
          data-testid="reset-password-submit"
        >
          {busy ? t("loading") : t("reset_submit")}
        </button>
      </form>
    </div>
  );
}
